import {
  DefaultError,
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import Router from 'next/router'
import { z } from 'zod'
import { signalAsyncExportLaunched } from '@/lib/export/asyncExportSignal'
import { getDomainSetting } from '@/lib/getDomainSettings'
import { isSandboxActive } from '@/lib/hook/sandboxStore'
import parseJsonLdToJson, { getAcceptType } from '@/lib/parseJsonLdToJson'
import { getStoredSessionPatient } from '@/lib/patient/patientSession'
import { useCurrentLocale } from '@/locales/index'
import { PATIENT_DOMAINS } from '@/types/enum/constants'
import { authClient, getSession } from '../auth/session'

/**
 * Défauts calibrés back-office (#680, recalibré) — avec les défauts v5 (`staleTime: 0`,
 * `retry: 3`), chaque montage ET chaque retour sur l'onglet relançaient TOUTES les
 * requêtes visibles, même vieilles de 2 secondes (IndustryDashboard : 8 useRestQuery).
 *
 * Le refetch au focus est CONSERVÉ : dans un back-office où on alt-tab en permanence,
 * c'est lui le rafraîchissement régulier — `staleTime: 60s` le rend simplement
 * sélectif (seules les queries de plus d'une minute repartent).
 *
 * La fraîcheur après écriture ne dépend pas de la staleness : `useRestMutation`
 * invalide toutes les queries après chaque mutation (voir
 * `invalidateAllQueriesAfterMutation`). Un écran « vivant » garde ses propres
 * options : `staleTime: 0` / `refetchInterval` localement.
 */
const RETRY_COUNT_ON_SERVER_ERROR = 1

/**
 * Une réponse 4xx dit que la REQUÊTE est refusée — la rejouer à l'identique la refait
 * refuser. Le `retry: 1` global doublait donc mécaniquement chaque erreur client, et sur un
 * endpoint durablement cassé cela donnait deux appels pour rien à chaque déclenchement
 * (constaté le 18/09/2026 : `GET /my-notifications` en 400, requêtes par paires dans le
 * profiler). Seules les erreurs réseau et les 5xx — celles qu'un second essai peut
 * réellement corriger — gardent leur relance.
 *
 * Le 401 est déjà rejoué UNE fois à l'intérieur de `fetchRest` (rafraîchissement du token) :
 * le laisser aussi au retry de TanStack revenait à retenter avec le même jeton périmé.
 */
const isClientError = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null | undefined)?.status

  // 408 (délai dépassé) et 429 (trop de requêtes) sont les deux 4xx qu'un second essai peut
  // réellement corriger : ils gardent la relance.
  if (status === 408 || status === 429) return false
  return typeof status === 'number' && status >= 400 && status < 500
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (failureCount: number, error: unknown) =>
        !isClientError(error) && failureCount < RETRY_COUNT_ON_SERVER_ERROR,
    },
  },
})

/**
 * Endpoints des compteurs « vivants » du menu. Litteraux (et non importes depuis
 * ApiQueryEndpoint) pour ne pas creer de cycle d'imports avec ce module, qui est la
 * base de toute la couche REST.
 */
const USER_ACTIONS_ENDPOINT = 'me/actions'
const MENU_BADGES_ENDPOINT = 'me/menu_badges'

/**
 * HISTORIQUE : il existait ici une liste explicite ENDPOINTS_TOUCHING_USER_ACTIONS des
 * mutations qui reglaient une action du registre /me/actions, seule source d'invalidation
 * des compteurs du menu. Elle est devenue inutile depuis que `useRestMutation` invalide
 * TOUTES les queries apres chaque mutation reussie (#680) — le registre et les badges,
 * montes en permanence dans le menu, sont couverts d'office.
 *
 * Savoir conserve : les flux qui QUITTENT l'application ne passent par aucune mutation
 * locale au moment ou l'action se realise — signature Universign / checkout Stripe
 * renvoient une URL externe suivie en `window.location.href`, et le retour par redirection
 * pleine page reconstruit les caches. Un flux qui partirait en `window.open` (nouvel
 * onglet) n'a PAS cette garantie : c'etait le cas du Checkout du mandat PLV, traite par
 * une detection de transition dans AutoModeActivation ; le refetch au focus de
 * /me/actions (staleTime 0) couvre aussi ce retour d'onglet.
 */

/** Rejoue les compteurs du menu (registre d'actions + badges agreges). */
export const invalidateUserActionsCache = () => {
  queryClient.invalidateQueries({ queryKey: [USER_ACTIONS_ENDPOINT] })
  queryClient.invalidateQueries({ queryKey: [MENU_BADGES_ENDPOINT] })
}

/**
 * Invalide TOUTES les queries apres une mutation reussie.
 *
 * C'est ce qui permet au `staleTime` global de 60 s de ne PAS figer les ecrans : avant
 * (staleTime 0), une liste se rafraichissait au remontage apres une edition ; maintenant,
 * c'est cette invalidation qui garantit la fraicheur apres CHAQUE ecriture.
 *
 * Pourquoi tout et pas « le meme domaine d'endpoint » : l'API expose la meme entite sous
 * plusieurs URI (PATCH `companies/:id` mais liste `pharmacies?`, users vs admins...) — un
 * rapprochement par prefixe rate ces correspondances (constate : nom de pharmacie edite,
 * liste /pharmacies restee perimee 60 s). Le cout reste borne : seules les queries
 * actuellement montees refetchent immediatement, les autres sont juste marquees stale et
 * se rechargeront a leur prochain montage — et une mutation est un evenement rare compare
 * aux navigations/focus que le staleTime protege.
 */
const invalidateAllQueriesAfterMutation = () => {
  queryClient.invalidateQueries()
}

interface UseRestQueryProps<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TVariables = unknown,
> extends Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey'> {
  endpoint: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  queryKey?: UseQueryOptions<TQueryFnData, TError, TData>['queryKey']
  variables?: TVariables
  queriesParams?: Record<string, string | number | boolean | (string | number)[]>
  customHeaders?: Record<string, string>
  baseUrl?: string
  /** Voir `buildHeaders` : requête attribuée à l'AGENT même pendant une impersonation. */
  ignoreSwitchUser?: boolean
}

interface UseRestMutationProps<
  TData = unknown,
  TError = DefaultError,
  TVariables = unknown,
  TOnMutateResult = unknown,
> extends Omit<
  UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
  'mutationKey' | 'mutationFn'
> {
  endpoint: string
  mutationKey?: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>['mutationKey']
  // ZodTypeAny (et non ZodType<TVariables>) : un hook peut typer TVariables explicitement
  // TOUT EN spreadant un endpoint qui porte son schéma — exiger l'équivalence exacte des
  // deux forçait à choisir entre types d'appel et validation runtime. Le parse valide
  // toujours à l'exécution.
  schema?: z.ZodTypeAny
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  queriesParams?: Record<string, string | number | boolean | (string | number)[]>
  variables?: Record<string, unknown>
  customHeaders?: Record<string, string>
  /** Voir `buildHeaders` : requête attribuée à l'AGENT même pendant une impersonation. */
  ignoreSwitchUser?: boolean
  /**
   * Cette requête DEMANDE un export asynchrone (202 + fichier généré en tâche de fond). Le
   * drapeau se déclare sur la définition de l'endpoint (`ApiMutationEndpoint`) et se propage
   * par le spread : aucun écran n'a de code de signalement. Au succès, la liste
   * « Téléchargements » du header passe en rafraîchissement rapide pendant quelques minutes
   * (cf. `lib/export/asyncExportSignal.ts`).
   */
  asyncExport?: boolean
  /**
   * Par défaut, une mutation réussie invalide toutes les queries (voir
   * invalidateAllQueriesAfterMutation). À passer à `false` quand l'appelant tient lui-même
   * son état à partir de la réponse et qu'aucune query montée n'a besoin d'être rejouée :
   * chaque query active repart sinon en requête réseau à chaque mutation. Vécu sur le
   * tunnel public du bilan de prévention : le GET eligibility, toujours actif, était rejoué
   * à chaque autosave, soit une fois par page du questionnaire, jusqu'au 429 du quota API
   * (30 appels par 5 min et par IP).
   */
  invalidateQueriesOnSuccess?: boolean
}

const BASE_URL = getDomainSetting('urlApiGroup')

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

// Vrai uniquement dans l'app patient (route /patient OU domaine patient). Le token patient
// (par-dossier) ne doit JAMAIS être utilisé pour des requêtes backoffice/frontoffice : sinon
// un patient_session résiduel en sessionStorage empoisonne toutes les requêtes admin (401).
// NB : sur les domaines patient, le pathname peut être réécrit (rewrite), d'où le test d'hôte.
const isPatientContext = (): boolean => {
  if (typeof window === 'undefined') return false
  return (
    window.location.pathname.startsWith('/patient') ||
    PATIENT_DOMAINS.includes(window.location.hostname)
  )
}

// Plusieurs requêtes peuvent prendre un 401 en même temps (accessToken expiré, ou course avec
// le refresh de boot). On déduplique les refresh concurrents via une promesse partagée, sinon
// chacune déclencherait son propre appel à /token/refresh pour un résultat identique.
//
// À noter : le refreshToken n'est PAS roté (`single_use: false` côté API, cf.
// config/packages/gesdinet_jwt_refresh_token.yaml). Des appels parallèles n'invalident donc
// rien aujourd'hui. La déduplication sert à ne pas mitrailler l'endpoint, et elle deviendra
// indispensable à la session le jour où la rotation sera activée.
let refreshPromise: Promise<void> | null = null
const refreshSessionOnce = (): Promise<void> => {
  if (!refreshPromise) {
    refreshPromise = authClient.updateSession().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

/**
 * Construit les headers d'authentification
 */
const buildHeaders = (
  method: HttpMethod = 'GET',
  accept?: string,
  customHeaders?: Record<string, string>,
  locale?: string,
  /**
   * N'ajoute PAS `X-Switch-User`, même pendant une impersonation. Réservé aux endpoints qui
   * décrivent l'activité de l'AGENT et non celle de la personne consultée — aujourd'hui les
   * trois `crm_calls` (le softphone Ringover reste branché pendant un « Se connecter en tant
   * que » : ses écritures doivent rester attribuées à l'agent, et ces opérations exigent
   * ROLE_ADMIN, donc partiraient en 403 sous l'identité du pharmacien).
   *
   * Un drapeau dédié et non un `customHeaders` : `Object.assign` peut ÉCRASER la valeur de
   * l'en-tête, jamais SUPPRIMER la clé — et un `X-Switch-User` vide ferait entrer le pare-feu
   * `switch_user` de l'API avec une identité vide.
   */
  ignoreSwitchUser?: boolean
): Record<string, string> => {
  const session = getSession()
  // Token patient consulté SEULEMENT en contexte patient — jamais en backoffice/frontoffice.
  const patientToken = isPatientContext() ? getStoredSessionPatient() : null

  let tokenToUse = session?.accessToken
  if (patientToken?.isAuthenticated) {
    const activeDossier = patientToken.activeDossierId
      ? patientToken.dossiers.find(d => d.dossierId === patientToken.activeDossierId)
      : patientToken.dossiers[0]
    tokenToUse = activeDossier?.token
  }

  const headers: Record<string, string> = {
    'Content-Type': method === 'PATCH' ? 'application/merge-patch+json' : 'application/json',
  }
  // POURQUOI le header est conditionnel : `tokenToUse` peut legitimement etre absent —
  // session pas encore hydratee (sessionStorage n'est PAS partage entre onglets : un nouvel
  // onglet demarre sans accessToken alors que le cookie refreshToken persiste, et le
  // `updateSession()` du constructeur AuthClient est fire-and-forget), ou dossier patient
  // sans token. Le template litteral produisait alors `Bearer null` / `Bearer undefined` :
  // Lexik voit un token PRESENT mais indecodable et repond 401 « Invalid JWT Token » avant
  // meme d'atteindre le controller, y compris sur les endpoints publics. Sans header, la
  // requete part anonyme — meme regle que `buildAuthHeaders` (lib/fetchBinary.ts).
  if (tokenToUse) headers['Authorization'] = `Bearer ${tokenToUse}`
  if (accept) headers['Accept'] = accept
  if (locale) headers['Accept-Language'] = locale
  if (session?.switchUser && !ignoreSwitchUser) headers['X-Switch-User'] = session.switchUser
  if (session?.careActorToken) headers['X-Care-Actor'] = session.careActorToken
  // Mode test Care : jamais en contexte patient (auth dediee, jamais de sandbox cote patient).
  if (!isPatientContext() && isSandboxActive()) headers['X-Care-Sandbox'] = '1'
  if (customHeaders) Object.assign(headers, customHeaders)
  return headers
}

/**
 * Résout les variables de chemin (:key) dans l'endpoint
 */
const resolvePathVariables = (endpoint: string, variables?: Record<string, unknown>): string => {
  if (!endpoint) {
    // Message explicite : un endpoint undefined vient presque toujours d'une cle
    // ApiQueryEndpoint/ApiMutationEndpoint inexistante OU d'un bundle navigateur
    // perime apres redemarrage du dev server (recharger la page).
    throw new Error('fetchRest: endpoint manquant (clé Api*Endpoint inconnue ou bundle périmé)')
  }
  let resolved = endpoint
  if (variables && typeof variables === 'object') {
    Object.entries(variables).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      resolved = resolved.replace(`:${key}`, String(value))
    })
  }
  return resolved
}

/**
 * Ajoute les query params à l'endpoint (gère les tableaux pour API Platform)
 */
const appendQueryParams = (
  endpoint: string,
  queriesParams?: Record<string, string | number | boolean | (string | number)[] | unknown>
): string => {
  let resolved = endpoint
  if (queriesParams && typeof queriesParams === 'object') {
    const separator = () => (resolved.includes('?') ? '&' : '?')
    Object.entries(queriesParams).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (Array.isArray(value)) {
        value.forEach(v => {
          resolved = `${resolved}${separator()}${key}[]=${encodeURIComponent(String(v))}`
        })
      } else {
        resolved = `${resolved}${separator()}${key}=${encodeURIComponent(String(value))}`
      }
    })
  }
  return resolved
}

// Locale applicative par défaut pour les appels impératifs (fetchRest sans `locale`).
// Sans header Accept-Language explicite, le navigateur envoie le sien (ex: fr-FR) et
// l'API sert la locale par défaut `fr` : les contenus traduits uniquement en fr-BE/nl-BE
// (formations School BE) reviennent vides. Router.locale reflète la locale next-international.
const resolveLocale = (locale?: string): string | undefined => {
  if (locale) return locale
  if (typeof window === 'undefined') return undefined
  try {
    return Router.locale
  } catch {
    return undefined
  }
}

/**
 * Fonction utilitaire pour effectuer des appels REST de manière impérative (sans hook React).
 * Peut être utilisée dans des callbacks, des fonctions throttle, etc.
 */
export const fetchRest = async <T = unknown>({
  endpoint,
  method = 'GET',
  variables,
  queriesParams,
  body,
  customHeaders,
  locale,
  baseUrl,
  fetchOptions,
  responseType = 'json',
  ignoreSwitchUser,
  asyncExport,
}: {
  endpoint: string
  method?: HttpMethod
  variables?: Record<string, unknown>
  queriesParams?: Record<string, string | number | boolean | (string | number)[] | unknown>
  body?: unknown
  customHeaders?: Record<string, string>
  locale?: string
  baseUrl?: string
  /** Voir `buildHeaders` : requête attribuée à l'AGENT même pendant une impersonation. */
  ignoreSwitchUser?: boolean
  /**
   * Demande d'export asynchrone : au succès, ouvre la fenêtre de rafraîchissement rapide de
   * la liste « Téléchargements » du header (`lib/export/asyncExportSignal.ts`). Porté ici et
   * pas seulement dans `useRestMutation` parce que plusieurs écrans demandent leur export par
   * un `fetchRest` impératif — certains en GET (liens de suivi de programme Sound / PLV).
   * À ne JAMAIS passer depuis `useRestQuery` : un refetch rouvrirait la fenêtre sans fin.
   */
  asyncExport?: boolean
  fetchOptions?: Omit<RequestInit, 'method' | 'headers' | 'body'>
  // 'text' : endpoints renvoyant du text/html ou texte brut (ex: apercu template email).
  responseType?: 'json' | 'text'
}): Promise<T> => {
  let resolvedEndpoint = resolvePathVariables(endpoint, variables)
  resolvedEndpoint = appendQueryParams(resolvedEndpoint, queriesParams)

  const { Accept, itemsPerPage, pagination } = getAcceptType(method, resolvedEndpoint)
  const url = `${baseUrl ?? BASE_URL}${resolvedEndpoint}`
  const serializedBody = method !== 'GET' && body ? JSON.stringify(body) : undefined

  // buildHeaders relit getSession() à chaque appel : après un refresh, le nouveau token
  // est automatiquement repris au retry.
  const send = () =>
    fetch(url, {
      ...fetchOptions,
      method,
      headers: buildHeaders(method, Accept, customHeaders, resolveLocale(locale), ignoreSwitchUser),
      body: serializedBody,
    })

  let response = await send()

  // Filet de sécurité : un 401 signifie token absent/expiré (course avec le refresh de boot,
  // accessToken en sessionStorage). On rafraîchit via le refreshToken puis on rejoue une fois.
  // Exclu en contexte patient (auth dédiée, refresh géré ailleurs).
  // Exclu si aucun refreshToken en session : inutile de tenter un refresh (utilisateur non connecté).
  if (response.status === 401 && !isPatientContext() && getSession()?.refreshToken) {
    try {
      await refreshSessionOnce()
      response = await send()
    } catch {
      // updateSession() déclenche lui-même le signOut/redirection si le refresh échoue.
    }
  }

  // Signalé sur le STATUT et non sur le corps : un export répond 202 avec des charges utiles
  // très variables selon l'écran (message texte, { status }, { queued, email }). Le seul fait
  // commun exploitable est « le serveur a accepté la demande ».
  if (asyncExport && response.ok) signalAsyncExportLaunched()

  // Mode texte : endpoint renvoyant du text/html (ou texte brut). On lit response.text()
  // et on retourne directement la chaine (pas de parse JSON-LD).
  if (responseType === 'text') {
    const text = await response.text()
    if (!response.ok) {
      const error = new Error(text || `HTTP ${response.status}`)
      // @ts-ignore
      error.status = response.status
      throw error
    }
    return text as T
  }

  let data: unknown = null
  if (response.status !== 204 && response.headers.get('content-length') !== '0') {
    data = await response.json()
  }

  if (!response.ok) {
    const error = new Error(
      // Corps JSON reduit a une CHAINE : c'est ce que produit `$this->json('message', 409)`
      // cote API, utilise par de nombreux controllers (inscription, imports...). Sans ce cas,
      // le message metier etait perdu et l'ecran affichait un « HTTP 409 » inexploitable.
      (typeof data === 'string' && data.trim() !== '' ? data : null) ||
        (data as any)?.detail ||
        (data as any)?.message ||
        (data as any)?.['hydra:description'] ||
        (data as any)?.error ||
        // Corps portant `errors` au PLURIEL, souvent un tableau : c'est la forme des
        // controllers qui refusent champ par champ (synchro Store, imports). Sans ce cas,
        // le motif du refus etait perdu et l'ecran affichait un « HTTP 400 » muet.
        (Array.isArray((data as any)?.errors)
          ? (data as any).errors.filter(Boolean).join(' — ')
          : (data as any)?.errors) ||
        `HTTP ${response.status}`
    )

    // @ts-ignore
    error.status = response.status
    // @ts-ignore
    error.data = data

    throw error
  }

  if (response.status === 204 || !data) {
    return { success: true } as T
  }

  if (Accept === 'application/ld+json') {
    return parseJsonLdToJson(data, itemsPerPage, pagination)
  }
  return data as T
}

/**
 * Clé de cache d'une requête REST : la MÊME pour `useRestQuery` et pour un `useQueries` qui
 * charge plusieurs ressources d'un coup (colonnes d'une comparaison de fiches) — une ressource
 * déjà chargée ailleurs n'est pas redemandée.
 */
export const restQueryKey = ({
  endpoint,
  method,
  variables,
  queriesParams,
  locale,
}: {
  endpoint: string
  method?: string
  variables?: unknown
  queriesParams?: unknown
  locale: string
}) => [endpoint, method, JSON.stringify(variables), JSON.stringify(queriesParams), locale]

export const useRestQuery = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TVariables = unknown,
>({
  endpoint,
  method,
  variables,
  queriesParams,
  customHeaders,
  baseUrl,
  ignoreSwitchUser,
  ...props
}: UseRestQueryProps<TQueryFnData, TError, TData, TVariables>) => {
  const currentLocale = useCurrentLocale()

  return useQuery<TQueryFnData, TError, TData>({
    queryFn: () =>
      fetchRest<TQueryFnData>({
        endpoint,
        method,
        variables: variables as Record<string, unknown>,
        queriesParams,
        customHeaders,
        locale: currentLocale,
        baseUrl,
        ignoreSwitchUser,
      }),
    queryKey: restQueryKey({ endpoint, method, variables, queriesParams, locale: currentLocale }),
    ...props,
  })
}

export const useRestMutation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = unknown,
  TOnMutateResult = unknown,
>({
  endpoint,
  schema,
  method = 'POST',
  queriesParams,
  variables,
  customHeaders,
  ignoreSwitchUser,
  asyncExport,
  invalidateQueriesOnSuccess = true,
  ...props
}: UseRestMutationProps<TData, TError, TVariables, TOnMutateResult>) => {
  const currentLocale = useCurrentLocale()
  const { onSuccess: callerOnSuccess, ...restProps } = props

  return useMutation<TData, TError, TVariables, TOnMutateResult>({
    mutationFn: async (data: TVariables) => {
      const _data = schema ? schema.parse(data) : data

      // Merge path variables: explicit variables prop takes priority, then data fields
      const pathVariables = {
        ...(data && typeof data === 'object' ? (data as Record<string, unknown>) : {}),
        ...variables,
      }

      return fetchRest<TData>({
        endpoint,
        method,
        variables: pathVariables,
        queriesParams,
        body: _data ?? {},
        customHeaders,
        locale: currentLocale,
        ignoreSwitchUser,
        asyncExport,
      })
    },
    mutationKey: [endpoint, currentLocale],
    ...restProps,
    // Compose avec le onSuccess de l'appelant : ce dernier reste appele, apres
    // l'invalidation, exactement comme avant l'ajout de ce comportement.
    onSuccess: (data, variables, context, mutation) => {
      // Couvre aussi /me/actions et /me/menu_badges (montes en permanence dans le menu) —
      // le cas touchesUserActions() n'a plus besoin d'un traitement dedie ici ;
      // invalidateUserActionsCache() reste utile pour les ecritures HORS useRestMutation.
      if (invalidateQueriesOnSuccess) invalidateAllQueriesAfterMutation()

      return callerOnSuccess?.(data, variables, context, mutation)
    },
  })
}
